// import React from 'react'

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import { Input } from "../components/ui/input";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { GeneralContext } from "../services/providers/GeneralProvider";
// import { axiosInstanceFast } from "../services/api/axiosInstance";
import axios from "axios";
import { getCookie } from "../utils/utils";
import { getSystemId } from "../services/api/commonApis";
import { useQuery } from "react-query";
import { useToast } from "../components/ui/use-toast";
import { axiosInstanceFast } from "../services/api/axiosInstance";

const uploadLicenseSchema = z.object({
  systemID: z
    .string({
      required_error: "System ID is required",
    })
    .min(1, { message: "System ID is required" }),
  licenseDocument: z.any({
    required_error: "License Document is required",
  }),
});

type UploadLicenseType = z.infer<typeof uploadLicenseSchema>;

function License() {
  const { toast } = useToast();
  const { theme } = useContext(GeneralContext);
  const logoPath =
    theme === "light" ? "/images/ls-logo-white.png" : "/images/linklogo.png";
  const logo = process.env.STATIC_PATH + logoPath;

  const [systemID, setSystemID] = useState<string>("");

  const { data, isLoading } = useQuery<{ system_id: string }>(
    ["systemId"],
    ({ signal }) => getSystemId(signal),
  );

  useEffect(() => {
    const license_details = async () => {
      try {
        const response = await axiosInstanceFast.get("/system/license");
        if (response.data.data.isActive === true) {
          location.href = "/";
        }
      } catch (error) {
        console.log(error);
      }
    };
    license_details();
  }, []);

  useEffect(() => {
    if (data?.system_id) {
      form.setValue("systemID", data.system_id);
      setSystemID(data.system_id);
    }
  }, [data]);

  const form = useForm<UploadLicenseType>({
    resolver: zodResolver(uploadLicenseSchema),
    defaultValues: {
      systemID: systemID,
      licenseDocument: null,
    },
  });

  const fileChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      form.setValue("licenseDocument", files[0] as any);
    } else {
      form.setValue("licenseDocument", null);
    }
  };

  const uploadLicense = async (data: UploadLicenseType) => {
    if (data.licenseDocument == null) {
      alert("Please upload a file");
      return;
    } else if (data.licenseDocument.type != "") {
      alert("Please upload a valid license file");
      return;
    }
    const formData = new FormData();
    formData.append("file", data.licenseDocument);
    const csrfToken = getCookie("csrftoken");
    try {
      const response = await axios.post(
        process.env.FASTAPI_URL + "/system/upload-license/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      if (response.data.status == 1) {
        setTimeout(() => {
          location.href = "/";
        }, 500);
      } else if (response.data.status == 0) {
        toast({
          title: "Success",
          description: response.data.message,
          variant: "success",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="bg-primary-background">
        <div className="container w-full h-16 flex justify-between text-primary py-5 px-8 relative shadow">
          <div className="basis-64 flex items-center">
            <Link to="/">
              <img src={logo} alt="LinkShadowLosgo" width={180} />
            </Link>
          </div>
        </div>
      </div>
      {!isLoading && systemID !== "" && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-full max-w-[75%]">
            <Card>
              <CardHeader>
                <CardTitle>
                  Please Upload License Document to Activate the System
                </CardTitle>
                <CardContent>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(uploadLicense)}>
                      <div className="w-[95%] px-2">
                        <FormField
                          control={form.control}
                          name="systemID"
                          render={({ field }) => (
                            <FormItem className="w-[95%] mt-4">
                              <FormLabel>System ID</FormLabel>
                              <FormControl>
                                <textarea
                                  readOnly
                                  {...field}
                                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                  autoComplete="off"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="licenseDocument"
                          render={() => (
                            <FormItem className="w-[95%] mt-4">
                              <FormLabel>License Document</FormLabel>
                              <FormControl>
                                <Input
                                  type="file"
                                  onChange={fileChangeHandler}
                                  className="file:text-gray-500"
                                  // {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="flex justify-center mt-4">
                          <Button type="submit" variant="default">
                            Update License
                          </Button>
                        </div>
                      </div>
                    </form>
                  </Form>
                </CardContent>
              </CardHeader>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}

export default License;
